import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../lib/utils';
import { notificationsAPI } from '../../lib/api/notificationApi';

// Test Component for NotificationsAPI
const NotificationsPage: React.FC = () => {
    const { user } = useAuth();

    // State for test configurations
    const [pushToken, setPushToken] = useState<string>('');
    const [serverNotifications, setServerNotifications] = useState<any[]>([]);
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [testResults, setTestResults] = useState<any[]>([]);
    const [preferences, setPreferences] = useState({
        marketing: true,
        updates: true,
        activity: true
    });

    // Form states
    const [testTitle, setTestTitle] = useState<string>('Test Notification');
    const [testBody, setTestBody] = useState<string>('This is a test notification');
    const [testData, setTestData] = useState<string>('{"type":"test","route":"/test"}');
    const [testUserId, setTestUserId] = useState<string>('');
    const [testUserIds, setTestUserIds] = useState<string>('');
    const [testTopic, setTestTopic] = useState<string>('');
    const [testScheduleDate, setTestScheduleDate] = useState<string>('');
    const [testRecurring, setTestRecurring] = useState<boolean>(false);
    const [testPattern, setTestPattern] = useState<string>('');
    const [selectedToken, setSelectedToken] = useState<string>('');
    const [testNotificationType, setTestNotificationType] = useState<string>('activity');
    const [testGoalId, setTestGoalId] = useState<string>('');
    const [page, setPage] = useState<number>(1);
    const [limit, setLimit] = useState<number>(10);

    // Filter states
    const [filterStatus, setFilterStatus] = useState<string>('');
    const [filterType, setFilterType] = useState<string>('');
    const [filterStartDate, setFilterStartDate] = useState<string>('');
    const [filterEndDate, setFilterEndDate] = useState<string>('');

    // Initialize component
    useEffect(() => {
        if (user) {
            setTestUserId(user.firebaseId);
            loadInitialData();
        }
    }, [user]);

    // Load initial data for testing
    const loadInitialData = async () => {
        try {
            setLoading(true);
            await loadNotificationHistory();
            await loadUnreadCount();

            // Get current token if it exists
            const currentToken = notificationsAPI.getCurrentToken();
            if (currentToken) {
                setPushToken(currentToken);
                setSelectedToken(currentToken);

                // Load preferences for this token
                try {
                    const prefsResponse = await notificationsAPI.getPreferences(currentToken);
                    if (prefsResponse && prefsResponse.preferences) {
                        setPreferences(prefsResponse.preferences);
                    }
                } catch (err) {
                    addTestResult('Failed to load preferences', err);
                }
            }
        } catch (error) {
            addTestResult('Error loading initial data', error);
        } finally {
            setLoading(false);
        }
    };

    // Helper to add test results
    const addTestResult = (action: string, result: any) => {
        const newResult = {
            id: Date.now(),
            timestamp: new Date(),
            action,
            result: typeof result === 'object' ? JSON.stringify(result, null, 2) : String(result),
            success: !(result instanceof Error)
        };

        setTestResults(prev => [newResult, ...prev].slice(0, 20));
    };

    // Load notification history
    const loadNotificationHistory = async () => {
        try {
            if (!user) return;

            const response = await notificationsAPI.getHistory(page, limit);
            setServerNotifications(response.notifications || []);
            addTestResult('Loaded notification history', response);
            return response;
        } catch (error) {
            addTestResult('Error loading notifications', error);
            setServerNotifications([]);
        }
    };

    // Load unread count
    const loadUnreadCount = async () => {
        try {
            // const response = await notificationsAPI.getUnreadCount();
            // setUnreadCount(response.unreadCount);
            // addTestResult('Loaded unread count', response);
            console.log('Unread count refreshed');
        } catch (error) {
            addTestResult('Error loading unread count', error);
        }
    };

    // Register a new token
    const handleRegisterToken = async () => {
        if (!pushToken) {
            addTestResult('Register token failed', 'No token provided');
            return;
        }

        try {
            setLoading(true);
            const response = await notificationsAPI.registerToken(pushToken);
            addTestResult('Registered token', response);
            setSelectedToken(pushToken);
        } catch (error) {
            addTestResult('Error registering token', error);
        } finally {
            setLoading(false);
        }
    };

    // Unregister a token
    const handleUnregisterToken = async () => {
        if (!selectedToken) {
            addTestResult('Unregister token failed', 'No token selected');
            return;
        }

        try {
            setLoading(true);
            const response = await notificationsAPI.unregisterToken(selectedToken);
            addTestResult('Unregistered token', response);

            // If this was the current token, clear it
            if (selectedToken === notificationsAPI.getCurrentToken()) {
                setPushToken('');
            }
            setSelectedToken('');
        } catch (error) {
            addTestResult('Error unregistering token', error);
        } finally {
            setLoading(false);
        }
    };

    // Update notification preferences
    const handleUpdatePreferences = async () => {
        if (!selectedToken) {
            addTestResult('Update preferences failed', 'No token selected');
            return;
        }

        try {
            setLoading(true);
            const response = await notificationsAPI.updatePreferences(selectedToken, preferences);
            addTestResult('Updated preferences', response);
        } catch (error) {
            addTestResult('Error updating preferences', error);
        } finally {
            setLoading(false);
        }
    };

    // Mark notification as read
    const handleMarkAsRead = async (notificationId: string) => {
        try {
            setLoading(true);
            const response = await notificationsAPI.markAsRead(notificationId);
            addTestResult(`Marked notification ${notificationId} as read`, response);

            // Refresh data
            await loadNotificationHistory();
            await loadUnreadCount();
        } catch (error) {
            addTestResult(`Error marking notification ${notificationId} as read`, error);
        } finally {
            setLoading(false);
        }
    };

    // Send notification to current user
    const handleSendToSelf = async () => {
        if (!user) {
            addTestResult('Send to self failed', 'No authenticated user');
            return;
        }

        try {
            setLoading(true);
            let dataObj;
            try {
                dataObj = JSON.parse(testData);
            } catch (e) {
                dataObj = {};
            }

            const response = await notificationsAPI.sendToUser(
                user.firebaseId,
                testTitle,
                testBody,
                dataObj
            );
            addTestResult('Sent notification to self', response);

            // Refresh notifications after a short delay
            setTimeout(() => {
                loadNotificationHistory();
                loadUnreadCount();
            }, 2000);
        } catch (error) {
            addTestResult('Error sending notification to self', error);
        } finally {
            setLoading(false);
        }
    };

    // Send notification to specific user
    const handleSendToUser = async () => {
        if (!user) {
            addTestResult('Send to user failed', 'No user ID provided');
            return;
        }

        try {
            setLoading(true);
            let dataObj;
            try {
                dataObj = JSON.parse(testData);
            } catch (e) {
                dataObj = {};
            }

            const response = await notificationsAPI.sendToUser(
                user.firebaseId,
                testTitle,
                testBody,
                dataObj
            );
            addTestResult(`Sent notification to user ${testUserId}`, response);
        } catch (error) {
            addTestResult(`Error sending notification to user ${testUserId}`, error);
        } finally {
            setLoading(false);
        }
    };

    // Send notification to multiple users
    const handleSendToUsers = async () => {
        if (!testUserIds) {
            addTestResult('Send to users failed', 'No user IDs provided');
            return;
        }

        try {
            setLoading(true);
            const userIdArray = testUserIds.split(',').map(id => id.trim());
            let dataObj;
            try {
                dataObj = JSON.parse(testData);
            } catch (e) {
                dataObj = {};
            }

            const response = await notificationsAPI.sendToUsers(
                userIdArray,
                testTitle,
                testBody,
                dataObj
            );
            addTestResult(`Sent notification to users: ${testUserIds}`, response);
        } catch (error) {
            addTestResult(`Error sending notification to users: ${testUserIds}`, error);
        } finally {
            setLoading(false);
        }
    };

    // Send notification to a topic (admin only)
    const handleSendToTopic = async () => {
        if (!testTopic) {
            addTestResult('Send to topic failed', 'No topic provided');
            return;
        }

        try {
            setLoading(true);
            let dataObj;
            try {
                dataObj = JSON.parse(testData);
            } catch (e) {
                dataObj = {};
            }

            // Use a direct request since this isn't in the standard API
            const response = await fetch('/api/notifications/send/topic', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    topic: testTopic,
                    title: testTitle,
                    body: testBody,
                    data: dataObj
                })
            }).then(res => res.json());

            addTestResult(`Sent notification to topic ${testTopic}`, response);
        } catch (error) {
            addTestResult(`Error sending notification to topic ${testTopic}`, error);
        } finally {
            setLoading(false);
        }
    };

    // Schedule a notification
    const handleScheduleNotification = async () => {
        if (!testScheduleDate) {
            addTestResult('Schedule notification failed', 'No date provided');
            return;
        }

        try {
            setLoading(true);
            const scheduleDate = new Date(testScheduleDate);
            let dataObj;
            try {
                dataObj = JSON.parse(testData);
            } catch (e) {
                dataObj = {};
            }

            // Prepare recipients structure
            const recipients: any = {};

            if (testUserId) {
                recipients.userId = testUserId;
            } else if (testUserIds) {
                recipients.userIds = testUserIds.split(',').map(id => id.trim());
            } else if (testTopic) {
                recipients.topic = testTopic;
            } else if (user) {
                recipients.userId = user.uid;
            }

            // Make direct request to correct endpoint
            const response = await fetch('/api/notifications/schedule', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    title: testTitle,
                    body: testBody,
                    data: dataObj,
                    recipients,
                    sendAt: scheduleDate.toISOString(),
                    isRecurring: testRecurring,
                    pattern: testRecurring ? testPattern : undefined
                })
            }).then(res => res.json());

            addTestResult(`Scheduled notification for ${scheduleDate.toString()}`, response);
        } catch (error) {
            addTestResult('Error scheduling notification', error);
        } finally {
            setLoading(false);
        }
    };

    // Process scheduled notifications (admin only)
    const handleProcessScheduled = async () => {
        try {
            setLoading(true);
            // Direct request to admin endpoint
            const response = await fetch('/api/notifications/process-scheduled', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            }).then(res => res.json());

            addTestResult('Processed scheduled notifications', response);
        } catch (error) {
            addTestResult('Error processing scheduled notifications', error);
        } finally {
            setLoading(false);
        }
    };

    // Send goal notification
    const handleSendGoalNotification = async () => {
        if (!testGoalId) {
            addTestResult('Send goal notification failed', 'No goal ID provided');
            return;
        }

        try {
            setLoading(true);
            const response = await notificationsAPI.sendGoalNotification(
                testGoalId,
                testTitle,
                testBody,
                testNotificationType as any
            );
            addTestResult(`Sent goal notification for goal ID ${testGoalId}`, response);
        } catch (error) {
            addTestResult(`Error sending goal notification for goal ID ${testGoalId}`, error);
        } finally {
            setLoading(false);
        }
    };

    // Send test notification (dev only)
    const handleSendTestNotification = async () => {
        if (!testUserId) {
            addTestResult('Send test notification failed', 'No user ID provided');
            return;
        }

        try {
            setLoading(true);
            let dataObj;
            try {
                dataObj = JSON.parse(testData);
            } catch (e) {
                dataObj = {};
            }

            const response = await notificationsAPI.sendTestNotification(
                testUserId,
                testTitle,
                testBody,
                dataObj,
                testNotificationType
            );
            addTestResult('Sent test notification', response);

            // Refresh notifications after a short delay
            setTimeout(() => {
                loadNotificationHistory();
                loadUnreadCount();
            }, 2000);
        } catch (error) {
            addTestResult('Error sending test notification', error);
        } finally {
            setLoading(false);
        }
    };

    // Get notification statistics (admin only)
    const handleGetStats = async () => {
        try {
            setLoading(true);

            let url = '/api/notifications/stats';
            const params = new URLSearchParams();

            if (filterStartDate) {
                params.append('startDate', filterStartDate);
            }

            if (filterEndDate) {
                params.append('endDate', filterEndDate);
            }

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await fetch(url).then(res => res.json());
            addTestResult('Got notification statistics', response);
        } catch (error) {
            addTestResult('Error getting notification statistics', error);
        } finally {
            setLoading(false);
        }
    };

    // Get a notification by ID
    const handleGetById = async (id: string) => {
        try {
            setLoading(true);
            const response = await fetch(`/api/notifications/${id}`).then(res => res.json());
            addTestResult(`Got notification ${id}`, response);
        } catch (error) {
            addTestResult(`Error getting notification ${id}`, error);
        } finally {
            setLoading(false);
        }
    };

    // UI Components
    const Card = ({ children, className = '' }) => (
        <div className={`bg-white dark:bg-gray-800 shadow-md rounded-lg p-4 mb-4 ${className}`}>
            {children}
        </div>
    );

    const Button = ({ onClick, children, disabled = false, variant = 'primary' }) => (
        <button
            className={`px-4 py-2 rounded transition-colors ${variant === 'primary'
                ? 'bg-blue-500 hover:bg-blue-600 text-white'
                : variant === 'danger'
                    ? 'bg-red-500 hover:bg-red-600 text-white'
                    : variant === 'success'
                        ? 'bg-green-500 hover:bg-green-600 text-white'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={onClick}
            disabled={disabled || loading}
        >
            {children}
        </button>
    );

    // Form input component
    const FormInput = ({ label, value, onChange, type = 'text', placeholder = '' }) => (
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {label}
            </label>
            <input
                key={label}
                type={type}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm 
                  focus:outline-none focus:ring-blue-500 focus:border-blue-500 
                  dark:bg-gray-800 dark:text-white"
            />
        </div>
    );

    // Form textarea component
    const FormTextarea = ({ label, value, onChange, rows = 3, placeholder = '' }) => (
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {label}
            </label>
            <textarea
                value={value}
                onChange={onChange}
                rows={rows}
                placeholder={placeholder}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm 
                  focus:outline-none focus:ring-blue-500 focus:border-blue-500 
                  dark:bg-gray-800 dark:text-white"
            />
        </div>
    );

    // Toggle component
    const Toggle = ({ label, checked, onChange }) => (
        <div className="flex items-center mb-4">
            <label className="flex items-center cursor-pointer">
                <div className="relative">
                    <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        onChange={onChange}
                    />
                    <div className={`block w-10 h-6 rounded-full ${checked ? 'bg-blue-500' : 'bg-gray-400'}`}></div>
                    <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition transform ${checked ? 'translate-x-4' : ''}`}></div>
                </div>
                <div className="ml-3 text-gray-700 dark:text-gray-300 font-medium">{label}</div>
            </label>
        </div>
    );

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-6">Notifications API Testing</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Token Management */}
                <Card>
                    <h2 className="text-xl font-semibold mb-4">Token Management</h2>

                    <FormInput
                        label="Device Token"
                        value={pushToken}
                        onChange={(e) => setPushToken(e.target.value)}
                        placeholder="Enter a FCM token to register"
                    />

                    <div className="flex space-x-2 mb-4">
                        <Button onClick={handleRegisterToken}>Register Token</Button>
                        <Button onClick={handleUnregisterToken} variant="danger">Unregister Token</Button>
                    </div>

                    <div className="mt-6">
                        <h3 className="text-lg font-medium mb-2">Notification Preferences</h3>

                        <Toggle
                            label="Marketing Messages"
                            checked={preferences.marketing}
                            onChange={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                        />

                        <Toggle
                            label="App Updates"
                            checked={preferences.updates}
                            onChange={() => setPreferences({ ...preferences, updates: !preferences.updates })}
                        />

                        <Toggle
                            label="Activity Notifications"
                            checked={preferences.activity}
                            onChange={() => setPreferences({ ...preferences, activity: !preferences.activity })}
                        />

                        <Button onClick={handleUpdatePreferences} className="mt-2">Update Preferences</Button>
                    </div>
                </Card>

                {/* Send Notifications */}
                <Card>
                    <h2 className="text-xl font-semibold mb-4">Send Notifications</h2>

                    <FormInput
                        label="Title"
                        value={testTitle}
                        onChange={(e) => setTestTitle(e.target.value)}
                        placeholder="Notification title"
                    />

                    <FormInput
                        label="Body"
                        value={testBody}
                        onChange={(e) => setTestBody(e.target.value)}
                        placeholder="Notification body"
                    />

                    <FormTextarea
                        label="Data (JSON)"
                        value={testData}
                        onChange={(e) => setTestData(e.target.value)}
                        placeholder='{"type":"test","route":"/test"}'
                    />

                    <FormInput
                        label="User ID (for sending to a specific user)"
                        value={testUserId}
                        onChange={(e) => setTestUserId(e.target.value)}
                        placeholder="User ID"
                    />

                    <FormInput
                        label="Multiple User IDs (comma-separated)"
                        value={testUserIds}
                        onChange={(e) => setTestUserIds(e.target.value)}
                        placeholder="id1,id2,id3"
                    />

                    <FormInput
                        label="Topic (for topic notifications - admin only)"
                        value={testTopic}
                        onChange={(e) => setTestTopic(e.target.value)}
                        placeholder="Enter topic name"
                    />

                    <div className="grid grid-cols-2 gap-2 mb-4">
                        <Button onClick={handleSendToSelf}>Send to Self</Button>
                        <Button onClick={handleSendToUser}>Send to User</Button>
                        <Button onClick={handleSendToUsers}>Send to Multiple Users</Button>
                        <Button onClick={handleSendToTopic}>Send to Topic</Button>
                        <Button onClick={handleSendTestNotification} variant="secondary">Send Test Notification</Button>
                    </div>
                </Card>

                {/* Goal Notifications */}
                <Card>
                    <h2 className="text-xl font-semibold mb-4">Goal Notifications</h2>

                    <FormInput
                        label="Goal ID"
                        value={testGoalId}
                        onChange={(e) => setTestGoalId(e.target.value)}
                        placeholder="Enter goal ID"
                    />

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Notification Type
                        </label>
                        <select
                            value={testNotificationType}
                            onChange={(e) => setTestNotificationType(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm 
                        focus:outline-none focus:ring-blue-500 focus:border-blue-500 
                        dark:bg-gray-800 dark:text-white"
                        >
                            <option value="reminder">Reminder</option>
                            <option value="milestone">Milestone</option>
                            <option value="completion">Completion</option>
                            <option value="activity">Activity</option>
                        </select>
                    </div>

                    <Button onClick={handleSendGoalNotification}>Send Goal Notification</Button>
                </Card>

                {/* Schedule Notifications */}
                <Card>
                    <h2 className="text-xl font-semibold mb-4">Schedule Notifications</h2>

                    <FormInput
                        label="Schedule Date/Time"
                        type="datetime-local"
                        value={testScheduleDate}
                        onChange={(e) => setTestScheduleDate(e.target.value)}
                    />

                    <Toggle
                        label="Recurring Notification"
                        checked={testRecurring}
                        onChange={() => setTestRecurring(!testRecurring)}
                    />

                    {testRecurring && (
                        <FormInput
                            label="Pattern (cron format)"
                            value={testPattern}
                            onChange={(e) => setTestPattern(e.target.value)}
                            placeholder="*/10 * * * *"
                        />
                    )}

                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Use the User ID field, Multiple User IDs field, or Topic field to specify recipients.
                    </p>

                    <div className="flex space-x-2">
                        <Button onClick={handleScheduleNotification}>Schedule Notification</Button>
                        <Button onClick={handleProcessScheduled} variant="secondary">Process Scheduled (Admin)</Button>
                    </div>
                </Card>

                {/* Admin Tools */}
                <Card>
                    <h2 className="text-xl font-semibold mb-4">Admin Tools</h2>

                    <div className="mb-4">
                        <h3 className="text-lg font-medium mb-2">Notification Stats</h3>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <FormInput
                                label="Start Date"
                                type="date"
                                value={filterStartDate}
                                onChange={(e) => setFilterStartDate(e.target.value)}
                            />

                            <FormInput
                                label="End Date"
                                type="date"
                                value={filterEndDate}
                                onChange={(e) => setFilterEndDate(e.target.value)}
                            />
                        </div>

                        <Button onClick={handleGetStats}>Get Statistics</Button>
                    </div>

                    <div className="mt-6">
                        <h3 className="text-lg font-medium mb-2">Filter Notifications</h3>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Status
                                </label>
                                <select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm 
                            focus:outline-none focus:ring-blue-500 focus:border-blue-500 
                            dark:bg-gray-800 dark:text-white"
                                >
                                    <option value="">All Statuses</option>
                                    <option value="sent">Sent</option>
                                    <option value="pending">Pending</option>
                                    <option value="failed">Failed</option>
                                    <option value="partial">Partial</option>
                                </select>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Type
                                </label>
                                <select
                                    value={filterType}
                                    onChange={(e) => setFilterType(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm 
                            focus:outline-none focus:ring-blue-500 focus:border-blue-500 
                            dark:bg-gray-800 dark:text-white"
                                >
                                    <option value="">All Types</option>
                                    <option value="individual">Individual</option>
                                    <option value="group">Group</option>
                                    <option value="topic">Topic</option>
                                    <option value="all">All Users</option>
                                </select>
                            </div>
                        </div>

                        <Button onClick={() => loadNotificationHistory()}>Apply Filters</Button>
                    </div>
                </Card>

                {/* Test Results */}
                <Card className="md:col-span-2">
                    <h2 className="text-xl font-semibold mb-4">Test Results</h2>

                    <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md">
                        {testResults.length === 0 ? (
                            <div className="p-4 text-gray-500 dark:text-gray-400 text-center">
                                No test results yet. Run some operations above to see results here.
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                {testResults.map(result => (
                                    <div key={result.id} className={`p-4 ${result.success ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                                        <div className="flex justify-between items-start mb-2">
                                            <span className="font-medium">
                                                {result.action}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {formatDate(result.timestamp)}
                                            </span>
                                        </div>
                                        <pre className="text-xs bg-white dark:bg-gray-800 p-2 rounded overflow-x-auto">
                                            {result.result}
                                        </pre>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="mt-4 flex justify-end">
                        <Button onClick={() => setTestResults([])} variant="secondary">Clear Results</Button>
                    </div>
                </Card>

                {/* Notification History */}
                <Card className="md:col-span-2">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">Notification History</h2>
                        <div className="flex space-x-2 items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                Unread: <span className="font-bold">{unreadCount}</span>
                            </span>
                            <Button
                                onClick={() => {
                                    loadNotificationHistory();
                                    loadUnreadCount();
                                }}
                                variant="secondary"
                            >
                                Refresh
                            </Button>
                        </div>
                    </div>

                    <div className="mb-4 flex space-x-4 items-center">
                        <div className="flex items-center space-x-2">
                            <label className="text-sm text-gray-700 dark:text-gray-300">Page:</label>
                            <input
                                type="number"
                                min="1"
                                value={page}
                                onChange={(e) => setPage(parseInt(e.target.value) || 1)}
                                className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-700 rounded"
                            />
                        </div>

                        <div className="flex items-center space-x-2">
                            <label className="text-sm text-gray-700 dark:text-gray-300">Limit:</label>
                            <input
                                type="number"
                                min="1"
                                max="50"
                                value={limit}
                                onChange={(e) => setLimit(parseInt(e.target.value) || 10)}
                                className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-700 rounded"
                            />
                        </div>

                        <Button onClick={loadNotificationHistory} variant="secondary">Apply</Button>
                    </div>

                    {serverNotifications.length === 0 ? (
                        <div className="text-center p-6 bg-gray-50 dark:bg-gray-900 rounded-md">
                            <p className="text-gray-500 dark:text-gray-400">No notifications found</p>
                        </div>
                    ) : (
                        <div className="space-y-4 max-h-96 overflow-y-auto">
                            {serverNotifications.map(notification => {
                                // Check if notification has been read
                                const isRead = notification.analytics?.openedBy?.some(read =>
                                    read.userId === user?.uid
                                );

                                return (
                                    <div
                                        key={notification._id}
                                        className={`p-4 rounded-md border ${isRead ? 'border-gray-200 dark:border-gray-700' : 'border-l-4 border-blue-500'} 
                              bg-white dark:bg-gray-800 hover:shadow-md transition-shadow`}
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <h3 className="font-bold text-lg">{notification.title || 'No Title'}</h3>
                                            <div className="flex items-center space-x-2">
                                                <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                                                    {formatDate(new Date(notification.createdAt))}
                                                </span>
                                                {!isRead && (
                                                    <Button onClick={() => handleMarkAsRead(notification._id)} variant="secondary">
                                                        Mark Read
                                                    </Button>
                                                )}
                                                <Button onClick={() => handleGetById(notification._id)} variant="secondary">
                                                    Details
                                                </Button>
                                            </div>
                                        </div>

                                        <p className="mb-2">{notification.body}</p>

                                        <div className="grid grid-cols-2 gap-4 mt-2 text-xs">
                                            <div className="text-gray-600 dark:text-gray-400">
                                                <div className="font-semibold">Status:</div>
                                                <span className={`px-2 py-1 rounded ${notification.status === 'sent' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                                    notification.status === 'failed' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                                                        notification.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                                                            'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                                    }`}>
                                                    {notification.status}
                                                </span>
                                            </div>

                                            <div className="text-gray-600 dark:text-gray-400">
                                                <div className="font-semibold">Type:</div>
                                                <span>{notification.type}</span>
                                            </div>
                                        </div>

                                        {notification.data && (
                                            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                                                <div className="font-semibold">Data:</div>
                                                <pre className="mt-1 pl-2 border-l-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2 rounded overflow-x-auto">
                                                    {JSON.stringify(notification.data, null, 2)}
                                                </pre>
                                            </div>
                                        )}

                                        {notification.recipients && (
                                            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                                                <div className="font-semibold">Recipients:</div>
                                                <div className="pl-2 border-l-2 border-gray-200 dark:border-gray-700">
                                                    {notification.recipients.userIds && (
                                                        <div>User IDs: {notification.recipients.userIds.join(', ')}</div>
                                                    )}
                                                    {notification.recipients.topic && (
                                                        <div>Topic: {notification.recipients.topic}</div>
                                                    )}
                                                    {notification.recipients.totalDevices > 0 && (
                                                        <div>Total devices: {notification.recipients.totalDevices}</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {notification.analytics && (
                                            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                                                <div className="font-semibold">Analytics:</div>
                                                <div className="pl-2 border-l-2 border-gray-200 dark:border-gray-700">
                                                    <div>Open Count: {notification.analytics.openCount || 0}</div>
                                                    {notification.analytics.openedBy && notification.analytics.openedBy.length > 0 && (
                                                        <div>
                                                            Opened By: {notification.analytics.openedBy.length} users
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {notification.schedule && notification.schedule.sendAt && (
                                            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                                                <div className="font-semibold">Schedule:</div>
                                                <div className="pl-2 border-l-2 border-gray-200 dark:border-gray-700">
                                                    <div>Send At: {formatDate(new Date(notification.schedule.sendAt))}</div>
                                                    {notification.schedule.isRecurring && (
                                                        <div>
                                                            <span>Recurring: Yes</span>
                                                            {notification.schedule.pattern && (
                                                                <span> (Pattern: {notification.schedule.pattern})</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                                            <span className="font-semibold">ID:</span> {notification._id}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default NotificationsPage;